/* eslint-disable react-hooks/exhaustive-deps */
import { Fragment, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, Paper, Skeleton, Typography } from "@mui/material";
import { ChoiceToggle, DocumentList, PageHeader, StepActions } from "@components/ui_v2";
import { RootState, useAppDispatch } from "@store/store";
import { useLazyGetPersonsQuery } from "@api/personasApi";
import { useLazyGetTabsConfigQuery } from "@api/catalogs";
import { useLazyGetDocumentAppIdQuery } from "@api/documents";
import { usePostValidateNextStageMutation } from "@api/applications";
import { ApiError, BofuTabDocument, PersonaStructure } from "@interfaces/response";
import {
  applyPersonDocumentChange,
  buildPersonDocuments,
  filterPersonsForFormHub,
  findPersonasTab,
  formatPersonName,
  PersonDocumentChange,
} from "@components/PersonForm/personHelpers";
import { BOFU_TABS_CONFIG } from "@helpers/constantes";
import { validateDocumentStructure } from "@components/TabBofu/functions";
import { setModalErrorVisible } from "@store/slices/appSlice";
import useStatusConfig from "@hooks/useStatusConfig";
import * as Sentry from "@sentry/react";
import { UUID } from "crypto";
import { STATUS_CATALOG } from "@helpers/constantes";

/** Un documento cuenta como cargado cuando tiene archivo; los múltiples, con al menos uno. */
const tieneArchivo = (docItem: BofuTabDocument) =>
  docItem.multiple ? docItem.list_values.some(Boolean) : Boolean(docItem.file_value);

export const ExpedientePFAE = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { nextStatus } = useStatusConfig(STATUS_CATALOG.avance_expediente_digital.code);
  const applicationData = useSelector((state: RootState) => state.register.application);
  const accountUser = useSelector((state: RootState) => state.register.account_user);
  const bofuLoading = useSelector((state: RootState) => state.bofu.isLoading);

  const [persons, setPersons] = useState<PersonaStructure[]>([]);
  const [documentsByPerson, setDocumentsByPerson] = useState<Record<string, BofuTabDocument[]>>({});
  const [selectedPersonId, setSelectedPersonId] = useState<string | null>(null);
  const [isLoadingPersons, setIsLoadingPersons] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [getPersons] = useLazyGetPersonsQuery();
  const [getTabsConfig] = useLazyGetTabsConfigQuery();
  const [getDocumentsByAppId] = useLazyGetDocumentAppIdQuery();
  const [triggerPostNextStage] = usePostValidateNextStageMutation();

  const selectedPerson = useMemo(
    () => persons.find((p) => p.id === selectedPersonId) || null,
    [persons, selectedPersonId],
  );

  const selectedDocuments = selectedPersonId ? documentsByPerson[selectedPersonId] || [] : [];

  const loadPersons = async () => {
    if (!applicationData?.id || !applicationData?.type) {
      setIsLoadingPersons(false);
      return;
    }

    try {
      setIsLoadingPersons(true);

      const [response, tabsConfig, uploadedDocuments] = await Promise.all([
        getPersons({ loanApplicationId: applicationData.id }).unwrap(),
        getTabsConfig({
          taxcode: applicationData.type,
          code: BOFU_TABS_CONFIG.tab_personas.code,
        }).unwrap(),
        getDocumentsByAppId({
          loanApplicationId: applicationData.id,
          status: true,
        }).unwrap(),
      ]);

      const personList = filterPersonsForFormHub(response || []);
      const tabDocuments = (findPersonasTab(tabsConfig)?.documents || []).filter(validateDocumentStructure);
      const nextDocuments = Object.fromEntries(
        personList.map((person) => [
          person.id,
          buildPersonDocuments({
            person,
            tabDocuments,
            uploadedDocuments: uploadedDocuments || [],
            clientDetailsId: applicationData.client_details_id as UUID,
            allPersons: personList,
          }),
        ]),
      );

      setPersons(personList);
      setDocumentsByPerson(nextDocuments);
      setSelectedPersonId((current) => {
        if (current && personList.some((p) => p.id === current)) return current;
        return personList[0]?.id || null;
      });
    } catch (error) {
      Sentry.captureException(error, {
        extra: { component: "ExpedientePFAE-loadPersons" },
      });
      setPersons([]);
      setDocumentsByPerson({});
      setSelectedPersonId(null);
    } finally {
      setIsLoadingPersons(false);
    }
  };

  useEffect(() => {
    loadPersons();
  }, [applicationData?.id, applicationData?.type]);

  const handleDocumentChange = (change: PersonDocumentChange) => {
    if (!selectedPersonId) return;
    setDocumentsByPerson((current) => ({
      ...current,
      [selectedPersonId]: applyPersonDocumentChange(current[selectedPersonId] || [], change),
    }));
  };

  const isLoading = bofuLoading || isLoadingPersons;

  const todosCompletos = useMemo(
    () =>
      persons.length > 0 &&
      Object.values(documentsByPerson).every((docs) => docs.filter((d) => d.required).every(tieneArchivo)),
    [persons, documentsByPerson],
  );

  const handleContinue = async () => {
    if (!todosCompletos || !nextStatus) return;

    try {
      setIsSubmitting(true);
      await triggerPostNextStage({
        applicationId: applicationData.id,
        statusCode: nextStatus.code,
        username: `TOFU - ${accountUser.email}`,
      }).unwrap();
      navigate(nextStatus.url);
    } catch (error) {
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      Sentry.captureException(error, {
        extra: { component: "ExpedientePFAE-handleContinue" },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box>
      <PageHeader
        eyebrow="EXPEDIENTE"
        title="Documentos de tu expediente"
        subtitle="Con esto tu ejecutivo prepara el contrato de arrendamiento. Puedes subirlos en varias sesiones."
      />

      {isLoading ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Skeleton variant="rectangular" height={48} sx={{ borderRadius: 2 }} />
          <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
        </Box>
      ) : persons.length === 0 ? (
        <Typography color="primary.dark" fontSize={16} textAlign="center" fontWeight={600} mt={3}>
          No se encontraron personas registradas
        </Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "22px" }}>
          {persons.length > 1 && (
            <ChoiceToggle
              value={selectedPersonId ?? ""}
              onChange={(v) => setSelectedPersonId(v as string)}
              options={persons.map((p) => ({
                value: p.id,
                label: formatPersonName(p),
              }))}
            />
          )}

          {selectedPerson && (
            <Fragment>
              {selectedDocuments.length > 0 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                  <Paper variant="lendia_paper" sx={{ p: "20px 22px" }}>
                    <DocumentList
                      key={selectedPerson.id}
                      documents={selectedDocuments}
                      onChange={(params) => handleDocumentChange(params)}
                      onChangeMultiple={(params) => handleDocumentChange(params)}
                    />
                  </Paper>
                </Box>
              )}
            </Fragment>
          )}

          <StepActions
            onNext={handleContinue}
            nextLabel="Enviar expediente"
            nextDisabled={!todosCompletos || isSubmitting}
            nextTestId="expediente-pfae-continue-button"
          />
        </Box>
      )}
    </Box>
  );
};
